import axios from 'axios';
import { API_BASE_URL, WEB_API_BASE_URL } from '../config/config';

interface GreenTagValidationResponse {
  success: boolean;
  exists?: boolean;
  underRegistration?: boolean;
  customer?: {
    uid?: string;
    UID?: string;
    cdeVerificationUID?: string;
    name: string;
    Name?: string;
    code?: string;
    Code?: string;
    number?: string;
    aliasName?: string;
    type?: string;
    address?: string;
    city?: string;
    province?: string;
    zipCode?: string;
    mobile?: string;
    phone?: string;
    latitude?: string;
    longitude?: string;
    serialno?: string;
    SerialNo?: string;
    greentag?: string;
    Greentag?: string;
    storeUID?: string;
  };
  registration?: {
    uid: string;
    greenTagNumber?: string;
    status?: string;
    custNum?: string;
    ownerName?: string;
    storeName?: string;
    registrationNumber?: string;
    regDate?: string;
  };
  sales?: {
    uid: string;
    loginId: string;
    empNo: string;
    name: string;
    email: string;
    contactNumber: string;
    roleName: string;
    orgName: string;
    orgUID: string;
  };
  message?: string;
}

interface AuthLoginResponse {
  token: string;
  userName: string;
  empUID: string;
  jobPositionUID: string;
  orgUID: string;
  orgTypeUID: string;  // Comes from MobileService auth response
  roleTypeUID: string; // Comes from MobileService auth response
  message: string;
  success: boolean;
  action?: any;
  status: number;
}

interface WebApiAuthResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  userName?: string;
  email?: string;
  authType?: string;
  SessionId?: string;
  EmpUID?: string;
  EmpName?: string;
  EmpNo?: string;
  OrgUID?: string;
  OrgName?: string;
  JobPositionUID?: string;
  UserRoleUID?: string;
  RoleName?: string;
}

class AuthService {
  private apiUrl = API_BASE_URL;
  private webApiUrl = WEB_API_BASE_URL;

  async validateGreenTag(greenTag: string): Promise<GreenTagValidationResponse> {
    try {
      // COMMENTED OUT - Old two-step authentication
      // const authResponse = await this.authenticateUser(greenTag);
      // if (!authResponse.success || !authResponse.token) {
      //   throw new Error(authResponse.message || 'Authentication failed. You are not authorized to access the web portal.');
      // }
      // localStorage.setItem('authToken', authResponse.token);
      // localStorage.setItem('empUID', authResponse.empUID || '');
      // localStorage.setItem('jobPositionUID', authResponse.jobPositionUID || '');
      // localStorage.setItem('orgUID', authResponse.orgUID || '');
      // localStorage.setItem('orgTypeUID', authResponse.orgTypeUID || '');
      // localStorage.setItem('roleTypeUID', authResponse.roleTypeUID || '');
      // localStorage.setItem('userType', 'customer');

      // NEW - Direct call to validate-customer
      const response = await axios.post(`${this.apiUrl}/CustomerPortal/validate-customer`, {
        GreenTagNumber: greenTag
      });


      // If customer exists, get Web API token using customer code
      if (response.data.success && response.data.customer && response.data.customer.code) {
        const customerCode = response.data.customer.code || response.data.customer.Code;
        const customerPassword = response.data.customer.custPwd || response.data.customer.Custpwd || response.data.customer.custpwd || 'Cocacola@123';

        try {
          // Authenticate with Web API to get bearer token
          const webApiAuth = await this.authenticateWithWebAPI(customerCode, customerPassword);

          if (webApiAuth.access_token) {
            localStorage.setItem('authToken', webApiAuth.access_token);
            localStorage.setItem('tokenType', webApiAuth.token_type || 'Bearer');
          }
        } catch (webApiError: any) {
          console.error('Web API authentication failed for customer:', webApiError);
          console.error('Customer Code:', customerCode);
          console.error('Error details:', webApiError.message);
          // Continue without Web API token - customer validation was successful
          // This allows the flow to continue even if Web API auth fails
        }
      }

      // Check if this is a sales user response
      if (response.data.success && response.data.sales) {
        // Store sales user data
        localStorage.setItem('userType', 'sales');
        localStorage.setItem('salesData', JSON.stringify(response.data.sales));

        // STEP 3: For sales users, also get Web API token
        try {
          const webApiToken = await this.authenticateWithWebAPI(response.data.sales.loginId, '1111');
          localStorage.setItem('webApiToken', webApiToken.access_token);
          localStorage.setItem('webApiUserData', JSON.stringify(webApiToken));
        } catch (webApiError: any) {
          console.error('Web API authentication failed for sales user:', webApiError);
          console.error('Error details:', webApiError.message);
          // Continue with MobileService token only - will handle gracefully in ticketService
        }
      } else if (response.data.success && response.data.exists && response.data.customer) {
        // Store customer data
        localStorage.setItem('userType', 'customer');
        localStorage.setItem('customerData', JSON.stringify(response.data.customer));
      }

      return response.data;
    } catch (error: any) {
      // Re-throw the error for proper error handling in the UI
      throw error;
    }
  }

  async authenticateUser(greenTag: string): Promise<AuthLoginResponse> {
    try {
      // Call Auth/login on the MobileService API using green tag as username
      const response = await axios.post(`${this.apiUrl}/Auth/login`, {
        userName: greenTag,  // Green tag number as username
        password: "1111",    // Fixed password
        usertype: "weblogin" // Add usertype parameter for web portal login
      });

      console.log('Auth response:', response.data);

      // Check if authentication was successful
      if (!response.data.success || !response.data.token) {
        throw new Error(response.data.message || 'Authentication failed');
      }

      return response.data;
    } catch (error: any) {
      console.error('Auth login failed:', error);

      // If it's an authentication error, throw with the message
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      throw error;
    }
  }

  async authenticateWithWebAPI(username: string, password: string): Promise<WebApiAuthResponse> {
    try {
      const response = await axios.post(`${this.webApiUrl.replace('/api', '')}/token`,
        new URLSearchParams({
          grant_type: 'password',
          username: username,
          password: password
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Web API authentication failed:', error);

      if (error.response?.data) {
        throw new Error(`Web API authentication failed: ${error.response.data.error_description || error.response.data.error || 'Unknown error'}`);
      }

      throw error;
    }
  }

  async login(username: string, password: string) {
    try {
      const response = await axios.post(`${this.apiUrl}/auth/login`, {
        username,
        password
      });
      
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('userData', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }


  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('greenTagNumber');
    localStorage.removeItem('customerData');
    localStorage.removeItem('salesData');
    localStorage.removeItem('userType');
    localStorage.removeItem('empUID');
    localStorage.removeItem('jobPositionUID');
    localStorage.removeItem('orgUID');
    localStorage.removeItem('orgTypeUID');
    localStorage.removeItem('roleTypeUID');
    localStorage.removeItem('webApiToken');
    localStorage.removeItem('webApiUserData');
  }

  getUserType() {
    return localStorage.getItem('userType') || 'customer';
  }

  getSalesData() {
    const salesData = localStorage.getItem('salesData');
    return salesData ? JSON.parse(salesData) : null;
  }

  getCustomerData() {
    const customerData = localStorage.getItem('customerData');
    return customerData ? JSON.parse(customerData) : null;
  }

  getCurrentUser() {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  }

  getWebApiToken() {
    return localStorage.getItem('webApiToken');
  }

  getWebApiUserData() {
    const webApiUserData = localStorage.getItem('webApiUserData');
    return webApiUserData ? JSON.parse(webApiUserData) : null;
  }

  isAuthenticated() {
    return !!localStorage.getItem('authToken');
  }
}

export default new AuthService();